import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { chamarEstoque } from '@/hooks/useStock';
import { toast } from '@/hooks/use-toast';
import { demandKeys } from '@/hooks/useStockDemands';
import { mensagemDoErro } from '@/components/stock/stockErrorMessages';
import { resumoDasReservas, type ReservaDaChegada } from '@/components/stock/reservaDaChegada';

/**
 * Recebimento de matéria-prima (Bloco A, 0083).
 *
 * Mesmo contrato do `useStock`: as rotas respondem 200 mesmo no erro de
 * negócio, e `chamarEstoque` converte `{ok:false}` num throw tipado para que as
 * telas usem `onError` normalmente.
 *
 * ⚠️ O rascunho da conferência vive no SERVIDOR (`inbound_receipt_units`), não
 * em estado local. É o que faz um F5 no meio de pesar 20 rolos não perder nada
 * — e por isso toda mutação de rolo invalida o detalhe em vez de mexer numa
 * lista em memória, que seria uma segunda verdade a divergir da primeira.
 */

export interface ChegadaPendente {
  id: string;
  source: 'COMPRA' | 'NF';
  gc_id: string;
  fornecedor_nome: string | null;
  data_documento: string | null;
  numero_documento: string | null;
  itens: Array<{
    descricao: string;
    gc_produto_id: string | null;
    variacao?: string | null;
    quantidade: number | null;
    valor_unitario: number | null;
  }>;
  qty_recebida: number;
  qty_documento: number | null;
  qty_faltante: number | null;
  unidade: string | null;
  /** RECEBIDA só vem na busca do "Ligar à nota" (2ª remessa de compra já recebida); a fila não a lista. */
  situacao: 'AGUARDANDO' | 'PARCIALMENTE_RECEBIDA' | 'RECEBIDA';
  conferencia_aberta: string | null;
  /** Empresa do grupo em que a compra foi lançada no GC (null em documento antigo do espelho). */
  loja_nome?: string | null;
  valor_total?: number | null;
  /** Nº da NF-e quando o GC o manda (`numero_documento` é o nº da COMPRA). */
  numero_nf?: string | null;
  /** Por item, o que ainda não chegou (só em documento recebido em parte). */
  itens_faltantes?: Array<{ descricao: string | null; unidade: string | null; faltam: number }>;
}

/** Uma nota da conferência (§13.49): a do cabeçalho e as dos itens, como o estoquista as reconhece. */
export interface DocumentoDaConferencia {
  gc_doc_ref: string;
  gc_doc_source: string | null;
  numero_documento: string | null;
  fornecedor_nome: string | null;
  /** Linhas da conferência ligadas a esta nota. Ausente = servidor antigo. */
  itens?: number;
}

/** Prévia do pareamento item a item (`previa:true`): nada é gravado. */
export interface PreviaDaNota {
  itens_doc: Array<{ idx: number; descricao: string; quantidade: number | null; unidade: string | null; gc_produto_id: string | null; variacao?: string | null }>;
  linhas: Array<{ item_id: string; material_nome: string | null; descricao_nf: string | null; qty_real: number | null; unidade: string | null }>;
  sugestao: Array<{ doc_idx: number; item_id: string; origem: 'AUTOMATICO' | 'PALAVRAS' }>;
}

/** Uma compra que o servidor ligou sozinho pelo número digitado na NF (`/nota`). */
interface NotaLigadaAuto { docRef: string; numero: string; compra: string; fornecedor: string | null }

export interface ConferenciaResumo {
  id: string;
  gc_doc_ref: string | null;
  gc_doc_source: string | null;
  fornecedor_nome: string | null;
  status: string;
  /** Traz o "Fechado sem nota fiscal: …" (§13.85) quando foi o caso. */
  observacao?: string | null;
  concluido_em?: string | null;
  criado_por: string | null;
  created_at: string;
  itens: number;
  rolos_pesados: number;
  /**
   * Os rolos REAIS da conferência concluída (o `rolos_pesados` é o rascunho e zera ao concluir): quantos
   * seguem no estoque (não CONSUMIDA) e quantos desses ainda não têm a etiqueta do fornecedor. Servidor
   * antigo não manda — ausente = comportamento de antes.
   */
  rolos_no_estoque?: number;
  rolos_sem_etiqueta?: number;
  /** O documento ligado como o estoquista o reconhece: nº da compra e fornecedor no GC. */
  documento_numero?: string | null;
  documento_fornecedor?: string | null;
  /** TODAS as notas ligadas (cabeçalho primeiro). Servidor antigo não manda: vale o par acima. */
  documentos?: DocumentoDaConferencia[];
  /** A NF digitada ao conferir uma compra feita no sistema (sem documento no GestãoClick). */
  numero_nf?: string | null;
  /**
   * Os itens da conferência (o que entrou; antes de conferir, o da nota) e os pedidos das demandas — "de onde
   * vem, para onde vai". Servidor antigo não manda.
   */
  itens_lista?: Array<{ nome: string; quantidade: number | null; unidade: string | null }>;
  pedidos?: string | null;
}

export interface RoloRascunho {
  id: string;
  barcode: string;
  supplier_barcode: string | null;
  peso: number;
  /** Código K… quando o pacote foi registrado num kit gola + punho (§13.92). */
  kit_barcode?: string | null;
}

export interface ItemConferencia {
  id: string;
  material_id: string | null;
  material_nome: string | null;
  material_unidade: string | null;
  material_tracking: string | null;
  /** GOLA/PUNHO/… — o kit gola + punho (§13.92) só junta gola e punho em pacote. */
  material_kind?: string | null;
  /** Nº do pedido da demanda casada (§13.86) — vai na etiqueta do pacote de gola/punho. */
  demanda_numero_pedido?: string | null;
  /** Item de compra AVULSA (sem retrabalho): agrupa com os pedidos do mesmo material (§13.94) — a parte dele vai livre ao estoque. */
  demanda_avulsa?: boolean;
  descricao_nf: string;
  gc_produto_id: string | null;
  qty_nf: number | null;
  qty_real: number | null;
  valor_unitario: number | null;
  gc_compra_id: string | null;
  justificativa: string | null;
  rolos: RoloRascunho[];
  /** Linha sem material de compra AVULSA pura: pode ser "Recebido sem estoque" (§13.94). */
  pode_sem_estoque?: boolean;
  /**
   * O material que já foi escolhido antes para esta mesma linha, deste mesmo
   * fornecedor. Só vem quando o item ainda não tem material — sugerir para
   * quem já escolheu seria ruído.
   */
  sugestao?: {
    material_id: string;
    material_nome: string;
    unidade: string | null;
    usos: number;
  } | null;
}

export interface ConferenciaDetalhe {
  id: string;
  gc_doc_ref: string | null;
  gc_doc_source: string | null;
  gc_fornecedor_id: string | null;
  fornecedor_nome: string | null;
  status: string;
  observacao: string | null;
  criado_por: string | null;
  created_at: string;
  /** A NF digitada ao conferir uma compra feita no sistema (sem documento no GestãoClick). */
  numero_nf?: string | null;
  itens: ItemConferencia[];
}

export const inboundKeys = {
  all: ['stock', 'inbound'] as const,
  fila: (todas = false, busca = '') => ['stock', 'inbound', 'fila', todas ? 'todas' : 'corte', busca] as const,
  conferencias: () => ['stock', 'inbound', 'conferencias'] as const,
  conferencia: (id?: string) => ['stock', 'inbound', 'conferencia', id ?? null] as const,
  filaSistema: ['stock', 'inbound', 'fila-sistema'] as const,
};

/**
 * A fila começa na data de corte (o dia em que o recebimento ligou). `todas`
 * mostra também as anteriores — é para o "Ligar à nota" de um recebimento
 * sem nota que pode ser de compra antiga.
 */
export function useFilaDeChegadas(ligado: boolean, todas = false, busca = '') {
  const q = busca.trim();
  return useQuery({
    queryKey: inboundKeys.fila(todas, q),
    queryFn: () => {
      const params = new URLSearchParams();
      if (todas) params.set('todas', '1');
      if (q) params.set('q', q);
      const qs = params.toString();
      return chamarEstoque<ChegadaPendente[]>(`/api/stock/inbound/queue${qs ? `?${qs}` : ''}`);
    },
    enabled: ligado,
    staleTime: 60000,
  });
}

/**
 * Uma compra feita NO SISTEMA que ainda não chegou (§13.73): o "Comprei" do Dobra não existe no
 * GestãoClick, então a fila de cima nunca a via. Um item por linha, já com o fornecedor, o que
 * foi comprado e o que já entrou.
 */
export interface ChegadaDoSistema {
  item_id: string;
  demanda_id: string;
  order_id: string | null;
  numero_pedido: string | null;
  cliente: string | null;
  material_id: string | null;
  material_nome: string | null;
  unidade: string | null;
  descricao: string | null;
  qty_planejada: number | null;
  qty_recebida: number;
  fornecedor: string | null;
  entrega_modo: 'FORNECEDOR_ENTREGA' | 'BUSCAR' | null;
  previsao_chegada: string | null;
  comprado_em: string | null;
  /** A conferência já aberta para este item (retomar, não abrir outra). */
  conferencia_aberta: string | null;
  /** A demanda tem OUTRA linha COMPRAR/MISTO: só então "Não vem mais" vale com nada recebido. */
  tem_outra_linha_comprada?: boolean;
}

export function useFilaDoSistema(ligado: boolean) {
  return useQuery({
    queryKey: inboundKeys.filaSistema,
    queryFn: () => chamarEstoque<ChegadaDoSistema[]>('/api/stock/inbound/queue-sistema'),
    enabled: ligado,
    staleTime: 60000,
  });
}

export function useConferenciasAbertas(ligado: boolean) {
  return useQuery({
    queryKey: inboundKeys.conferencias(),
    // ⚠️ SÓ EM_CONFERENCIA. Sem o filtro a rota devolvia também AGUARDANDO_NF —
    // recebimento já CONCLUÍDO, saldo já lançado, só sem a nota — e ele
    // aparecia como "conferência em andamento" com seta de retomar. Retomar
    // abria o wizard de um recibo fechado: rolos apagados, Concluir cinza,
    // "pesar" devolvendo RECEBIMENTO_JA_FECHADO.
    queryFn: () => chamarEstoque<ConferenciaResumo[]>('/api/stock/receipts?status=EM_CONFERENCIA'),
    enabled: ligado,
    staleTime: 30000,
  });
}

/** Recebimentos concluídos sem documento — o saldo já entrou, falta a nota. */
export function useAguardandoNota(ligado: boolean) {
  return useQuery({
    queryKey: [...inboundKeys.all, 'aguardando-nf'],
    queryFn: () => chamarEstoque<ConferenciaResumo[]>('/api/stock/receipts?status=AGUARDANDO_NF'),
    enabled: ligado,
    staleTime: 30000,
  });
}

/** Ligados a uma nota nos últimos 15 dias — de onde se troca um vínculo feito com a nota errada. */
export function useLigadosRecentes(ligado: boolean) {
  return useQuery({
    queryKey: [...inboundKeys.all, 'ligados-recentes'],
    queryFn: () => chamarEstoque<ConferenciaResumo[]>('/api/stock/receipts?status=CONCLUIDO,CONCLUIDO_DIVERGENTE&com_documento=1'),
    enabled: ligado,
    staleTime: 30000,
  });
}

/** Fechados SEM nota (§13.85): CONCLUIDO sem documento — de onde ainda se liga a nota que apareceu depois. */
export function useFechadosSemNota(ligado: boolean) {
  return useQuery({
    queryKey: [...inboundKeys.all, 'fechados-sem-nf'],
    queryFn: () => chamarEstoque<ConferenciaResumo[]>('/api/stock/receipts?status=CONCLUIDO,CONCLUIDO_DIVERGENTE&sem_documento=1'),
    enabled: ligado,
    staleTime: 30000,
  });
}

export function useConferencia(id?: string) {
  return useQuery({
    queryKey: inboundKeys.conferencia(id),
    queryFn: () => chamarEstoque<ConferenciaDetalhe>(`/api/stock/receipts/${id}`),
    enabled: !!id,
    staleTime: 0,
  });
}

function avisarErro(err: Error & { code?: string }) {
  const m = mensagemDoErro(err.code);
  toast({ title: m.titulo, description: m.oQueFazer, variant: 'destructive' });
}

export function useRecebimentoMutations(conferenciaId?: string) {
  const qc = useQueryClient();
  const invalidar = () => {
    qc.invalidateQueries({ queryKey: inboundKeys.all });
    // O saldo muda na conclusão; sem isto a tela de saldos ao lado mentiria
    // até o próximo staleTime.
    qc.invalidateQueries({ queryKey: ['stock', 'balances'] });
  };

  const abrir = useMutation({
    mutationFn: (input: {
      gc_doc_ref?: string | null;
      gc_doc_source?: string | null;
      gc_fornecedor_id?: string | null;
      fornecedor_nome?: string | null;
      /** "Conferir chegada" de uma compra feita no sistema: os itens esperados e a NF digitada. */
      demand_item_ids?: string[];
      numero_nf?: string | null;
    }) => chamarEstoque<{ id: string; status: string }>('/api/stock/receipts', {
      method: 'POST', body: input,
    }),
    onSuccess: () => invalidar(),
    onError: avisarErro,
  });

  // A NF digitada de uma compra feita no sistema (sem documento no GestãoClick). Vale em qualquer
  // momento da conferência e depois dela — a mercadoria chega antes do papel; vazio apaga.
  const registrarNota = useMutation({
    mutationFn: (input: { receiptId: string; numero_nf: string }) =>
      chamarEstoque<{ numero_nf: string | null; ligadas?: NotaLigadaAuto[] }>(`/api/stock/receipts/${input.receiptId}/nota`, {
        method: 'POST', body: { numero_nf: input.numero_nf },
      }),
    // §13.49: o servidor liga sozinho a compra do GC de mesmo número e fornecedor; as mesmas chaves do
    // "Ligar à nota" manual (`invalidar`) já cobrem a conferência, as listas e a fila.
    onSuccess: (r) => {
      invalidar();
      const ligadas = r?.ligadas ?? [];
      toast(ligadas.length
        ? {
            title: ligadas.length === 1
              ? `✅ Nota ligada à compra nº ${ligadas[0].compra} do GestãoClick`
              : `✅ Notas ligadas às compras nº ${ligadas.map((l) => l.compra).join(' e ')} do GestãoClick`,
            description: ligadas[0].fornecedor ?? undefined,
          }
        : { title: '✅ Nota fiscal registrada' });
    },
    onError: avisarErro,
  });

  // A ÚNICA saída de AGUARDANDO_NF: casa o recebimento já lançado com o
  // documento que apareceu depois na fila. A rota existia desde o Bloco A e
  // nenhuma tela a chamava — "chegou sem nota" era beco sem saída.
  const vincularDocumento = useMutation({
    // `adicionar`: liga MAIS UMA nota a um recebimento que já tem (§13.49). `de`: com várias notas, qual delas o
    // `trocar` troca (sem ele, a do cabeçalho).
    // `previa`: só LÊ os itens da nota e as linhas livres (nada é gravado; devolve `{ previa }` (a rota responde `data:{previa}`), sem toast nem
    // invalidação). `pares`: liga exatamente estes itens da nota a estas linhas da conferência.
    mutationFn: (input: {
      receiptId: string; gc_doc_ref: string; gc_doc_source: string | null; trocar?: boolean; adicionar?: boolean; de?: string;
      previa?: boolean; pares?: Array<{ item_id: string; doc_idx: number }>;
    }) =>
      chamarEstoque<{ status?: string; previa?: PreviaDaNota }>(`/api/stock/receipts/${input.receiptId}/vincular-documento`, {
        method: 'POST',
        body: {
          gc_doc_ref: input.gc_doc_ref, gc_doc_source: input.gc_doc_source,
          ...(input.trocar ? { trocar: true } : {}), ...(input.adicionar ? { adicionar: true } : {}), ...(input.de ? { de: input.de } : {}),
          ...(input.previa ? { previa: true } : {}), ...(input.pares ? { pares: input.pares } : {}),
        },
      }),
    onSuccess: (_r, input) => {
      if (input.previa) return;
      invalidar();
      if (input.trocar) toast({ title: '✅ Nota trocada' });
      else if (input.adicionar) toast({ title: '✅ Nota ligada a este recebimento' });
    },
    onError: avisarErro,
  });

  // "Dar como recebida" (§13.49): a compra chegou toda mas a conta não fecha (gola por unidade
  // faturada em kg). Sai da fila com a justificativa; o saldo não muda.
  const darComoRecebida = useMutation({
    mutationFn: (input: { compraId: string; motivo: string }) =>
      chamarEstoque<{ status: string }>(`/api/stock/inbound/compras/${input.compraId}/dar-como-recebida`, {
        method: 'POST', body: { motivo: input.motivo },
      }),
    onSuccess: () => { invalidar(); toast({ title: '✅ Compra dada como recebida' }); },
    onError: avisarErro,
  });

  // "Fechar sem nota" (§13.85): a compra não vai ter nota; sai de "Recebidos sem nota" com o motivo.
  const fecharSemNota = useMutation({
    mutationFn: (input: { receiptId: string; motivo: string }) =>
      chamarEstoque<{ status: string }>(`/api/stock/receipts/${input.receiptId}/fechar-sem-nota`, {
        method: 'POST', body: { motivo: input.motivo },
      }),
    onSuccess: () => { invalidar(); toast({ title: '✅ Recebimento fechado sem nota' }); },
    onError: avisarErro,
  });

  const acrescentarItem = useMutation({
    mutationFn: (input: Record<string, unknown>) =>
      chamarEstoque<{ id: string }>(`/api/stock/receipts/${conferenciaId}/items`, {
        method: 'POST', body: input,
      }),
    onSuccess: () => invalidar(),
    onError: avisarErro,
  });

  const atualizarItem = useMutation({
    mutationFn: ({ itemId, patch }: { itemId: string; patch: Record<string, unknown> }) =>
      chamarEstoque(`/api/stock/receipts/${conferenciaId}/items/${itemId}`, {
        method: 'PATCH', body: patch,
      }),
    // onSettled: o grupo de agregado (§13.94) grava uma linha por vez, e se uma falha a tela relê o que ficou.
    onSettled: () => invalidar(),
    onError: avisarErro,
  });

  const pesarRolo = useMutation({
    mutationFn: (input: { receipt_item_id: string; peso: number; supplier_barcode?: string | null }) =>
      chamarEstoque<RoloRascunho>(`/api/stock/receipts/${conferenciaId}/units`, {
        method: 'POST', body: input,
      }),
    onSuccess: () => invalidar(),
    onError: avisarErro,
  });

  /** Kit gola + punho (§13.92): um pacote por item, todos com a mesma etiqueta K. */
  const registrarKit = useMutation({
    mutationFn: (input: { itens: Array<{ receipt_item_id: string; qtd: number }> }) =>
      chamarEstoque<{ kit_barcode: string; volumes: Array<RoloRascunho & { receipt_item_id: string }> }>(
        `/api/stock/receipts/${conferenciaId}/kits`, { method: 'POST', body: input }),
    onSuccess: () => invalidar(),
    onError: avisarErro,
  });

  const removerRolo = useMutation({
    mutationFn: (unitId: string) =>
      chamarEstoque<{ barcode: string }>(`/api/stock/receipts/${conferenciaId}/units/${unitId}`, {
        method: 'DELETE',
      }),
    onSuccess: () => invalidar(),
    onError: avisarErro,
  });

  /**
   * Tira da conferência uma linha que não é material de estoque.
   *
   * A nota traz frete, embalagem e serviço junto com a malha, e a conclusão
   * recusa qualquer linha sem material. A mensagem mandava removê-la e não
   * havia como: o estoquista, com os rolos já pesados, só tinha saídas ruins
   * (ligar o frete a um material qualquer, ou descartar tudo e recomeçar).
   */
  const removerItem = useMutation({
    mutationFn: (itemId: string) =>
      chamarEstoque<{ descricao: string | null }>(
        `/api/stock/receipts/${conferenciaId}/items/${itemId}`, { method: 'DELETE' },
      ),
    onSuccess: () => invalidar(),
    onError: avisarErro,
  });

  const concluir = useMutation({
    // `sem_resto`: ids das linhas cuja falta NÃO vem mais do fornecedor — a compra fecha com o que chegou.
    // `sem_estoque`: linhas de compra avulsa recebidas sem entrar no estoque, cada uma com o motivo (§13.94).
    mutationFn: (corpo?: { sem_resto?: string[]; sem_estoque?: Array<{ id: string; motivo: string }> }) =>
      chamarEstoque<{ status: string; rolos: number; itens: number; sem_estoque?: number; reservas_automaticas?: ReservaDaChegada[]; reserva_automatica_falhou?: boolean }>(
        `/api/stock/receipts/${conferenciaId}/complete`, { method: 'POST', ...(corpo ? { body: corpo } : {}) },
      ),
    onSuccess: (d) => {
      invalidar();
      const reservado = resumoDasReservas(d.reservas_automaticas);
      const falhou = d.reserva_automatica_falhou ? 'Não deu para separar a malha para os pedidos que esperavam: ela entrou livre — avise o comprador para reservar à mão.' : null;
      toast({
        title: d.status === 'CONCLUIDO_DIVERGENTE' ? '✅ Recebido, com divergência anotada' : '✅ Recebimento concluído',
        // Aviamento entra por quantidade, sem rolo: "0 rolo(s) entraram" soava
        // como se nada tivesse entrado.
        description: [
          d.rolos > 0
            ? `${d.rolos} volume(s) etiquetado(s) (rolos ou pacotes) entraram no estoque.`
            : d.itens > 0 ? `${d.itens} item(ns) entraram no estoque por quantidade.` : null,
          d.sem_estoque ? `${d.sem_estoque} item(ns) recebido(s) sem estoque.` : null,
          reservado,
          falhou,
        ].filter(Boolean).join(' '),
        // O que foi reservado para cada pedido precisa de tempo para ser lido (e o toast padrão some antes).
        ...(reservado || falhou ? { duration: 15000 } : {}),
      });
    },
    onError: avisarErro,
  });

  // As conferências VAZIAS (abertas e abandonadas, sem item): saem todas de uma vez da lista do posto.
  const descartarVazias = useMutation({
    mutationFn: async (ids: string[]) => {
      for (const id of ids) await chamarEstoque(`/api/stock/receipts/${id}/cancel`, { method: 'POST' });
    },
    onSuccess: (_r, ids) => {
      invalidar();
      toast({ title: ids.length === 1 ? 'Conferência vazia descartada' : `${ids.length} conferências vazias descartadas` });
    },
    onError: (err: Error & { code?: string }) => { invalidar(); avisarErro(err); },
  });

  const descartar = useMutation({
    mutationFn: () =>
      chamarEstoque(`/api/stock/receipts/${conferenciaId}/cancel`, { method: 'POST' }),
    onSuccess: () => {
      invalidar();
      toast({
        title: 'Conferência descartada',
        description: 'Nenhum rolo entrou no estoque. Jogue fora as etiquetas já impressas.',
      });
    },
    onError: avisarErro,
  });

  return {
    vincularDocumento, darComoRecebida, registrarNota, fecharSemNota, abrir, acrescentarItem, atualizarItem, pesarRolo, registrarKit, removerRolo, removerItem,
    concluir, descartar, descartarVazias };
}

// ─────────────────────────────────────────────────────────────────────────────
// Pendências
// ─────────────────────────────────────────────────────────────────────────────

export interface PendenciaEstoque {
  id: string;
  type: string;
  status: string;
  titulo: string;
  detalhe: string | null;
  metadata: Record<string, unknown>;
  order_id: string | null;
  production_step_id: string | null;
  material_id: string | null;
  material_nome: string | null;
  /** LISA se produz; MALHA/AVIAMENTO/GOLA/PUNHO se compra — decide o clique do estoque mínimo. */
  material_kind?: 'MALHA' | 'AVIAMENTO' | 'GOLA' | 'PUNHO' | 'LISA' | null;
  rolo_barcode: string | null;
  numero_pedido: string | null;
  cliente: string | null;
  step_setor: string | null;
  step_operador_id: string | null;
  step_operador_nome: string | null;
  step_concluido_em: string | null;
  /** O motivo que o cortador escreveu ao concluir o corte sem bipar rolo (LANCAR_CORTE). */
  step_motivo_conclusao?: string | null;
  /** DIVERGENCIA_RECEBIMENTO: o recebimento por trás (fornecedor, NF, compras) e, nas pendências antigas, os itens calculados na leitura. */
  recebimento?: {
    fornecedor_nome: string | null;
    numero_nf: string | null;
    concluido_por: string | null;
    concluido_em: string | null;
    documentos: DocumentoDaConferencia[];
    itens: ItemDivergente[] | null;
  } | null;
  resolution_note: string | null;
  resolved_by: string | null;
  resolved_at: string | null;
  created_at: string;
}

/** Um item que veio diferente da nota (DIVERGENCIA_RECEBIMENTO): no `metadata.itens` ou calculado na leitura. */
export interface ItemDivergente {
  item: string | null;
  qty_nf: number | string | null;
  qty_real: number | string | null;
  unidade?: string | null;
  /** Nº do(s) pedido(s) da demanda; ausente quando a compra não é de pedido. */
  pedido?: string | null;
  justificativa?: string | null;
  /** "Não vem mais": a falta foi encerrada com o que chegou. */
  fechada?: boolean;
}

export const pendingKeys = {
  all: ['stock', 'pending-tasks'] as const,
  lista: (tipos?: string[], status?: string, resolvidasDesde?: string) =>
    ['stock', 'pending-tasks', tipos?.join(',') ?? 'todos', status ?? 'ABERTA', resolvidasDesde ?? ''] as const,
};

/**
 * `resolvidasDesde` (ISO) traz, além do `status` pedido, as RESOLVIDAS e
 * DESCARTADAS a partir desse instante — o que o gerente já decidiu hoje
 * (Exceções do dia, §13.74). O filtro é do servidor: a lista corta em 200.
 */
export function usePendenciasEstoque(tipos?: string[], status = 'ABERTA', habilitado = true, resolvidasDesde?: string) {
  return useQuery({
    queryKey: pendingKeys.lista(tipos, status, resolvidasDesde),
    enabled: habilitado,
    queryFn: () => {
      const qs = new URLSearchParams({ status });
      if (tipos?.length) qs.set('tipos', tipos.join(','));
      if (resolvidasDesde) qs.set('resolvidas_desde', resolvidasDesde);
      return chamarEstoque<PendenciaEstoque[]>(`/api/stock/pending-tasks?${qs}`);
    },
    staleTime: 60000,
  });
}

export function useResolverPendencias() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { ids: string[]; acao: 'RESOLVER' | 'DESCARTAR'; resolution_note?: string }) =>
      chamarEstoque<{ atualizadas: number }>('/api/stock/pending-tasks', {
        method: 'PATCH', body: input,
      }),
    onSuccess: (d, vars) => {
      qc.invalidateQueries({ queryKey: pendingKeys.all });
      toast({
        title: vars.acao === 'RESOLVER'
          ? `✅ ${d.atualizadas} pendência(s) resolvida(s)`
          : `${d.atualizadas} pendência(s) descartada(s)`,
      });
    },
    onError: avisarErro,
  });
}


/**
 * Contagem de pendências abertas por tipo, para o AlertsSidebar do gerente.
 *
 * ⚠️ Chave FORA do prefixo `['stock']`: o sidebar é montado em toda aba de
 * gerente e não pode refetchar a cada bipe do galpão. Quem a invalida é o
 * listener de `stock_pending_tasks`, o único evento que muda a contagem.
 */
export function useContagemPendenciasEstoque(ligado = true) {
  return useQuery({
    queryKey: ['stock-alerts', 'count'],
    queryFn: () => chamarEstoque<Record<string, number>>('/api/stock/pending-tasks/count'),
    enabled: ligado,
    staleTime: 5 * 60_000,
    refetchOnWindowFocus: false,
  });
}


/**
 * "Não vem mais" na fila de "Compras a caminho" do sistema (§13.94): o item chegou em parte e o fornecedor não manda o
 * resto — a compra fecha com o que já chegou. Mexe na demanda (some da fila, o card de Compras acompanha), não no saldo.
 */
export function useNaoVemMaisDaCompra() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ itemId, motivo }: { itemId: string; motivo: string }) =>
      chamarEstoque<{ demanda_fechada: boolean; qty_planejada: number }>(
        `/api/stock/inbound/queue-sistema/${itemId}/nao-vem-mais`, { method: 'POST', body: { motivo } },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: inboundKeys.all });
      qc.invalidateQueries({ queryKey: demandKeys.all });
      toast({ title: '✅ Fechado: não vem mais' });
    },
    onError: avisarErro,
  });
}
